"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Card, CardBody, CardHeader, Badge, Input, Row, Col } from "reactstrap"
import { FaEdit, FaTrash, FaPlus, FaSearch, FaBan } from "react-icons/fa"
import DataTable, { type TableColumn } from "react-data-table-component"
import { Btn } from "../../../AbstractElements"
import { TurnstileAssignmentService } from "../../../Service/TurnstileService"
import type { TurnstileAssignment, PaginatedResponse } from "../turnstile/types"
import { toast } from "react-toastify"
import Swal from "sweetalert2"

interface Props {
  onAdd: () => void
  onEdit: (assignment: TurnstileAssignment) => void
  refreshTrigger: boolean
  onRefresh: () => void
}

const TurnstileAssignmentList: React.FC<Props> = ({ onAdd, onEdit, refreshTrigger, onRefresh }) => {
  const [assignments, setAssignments] = useState<TurnstileAssignment[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    pageSize: 10,
  })

  const fetchAssignments = async (page = 1, searchTerm = "", status = "") => {
    try {
      setLoading(true)
      const response: PaginatedResponse<TurnstileAssignment> = await TurnstileAssignmentService.getAll(
        page,
        pagination.pageSize,
        searchTerm,
        status,
      )
      setAssignments(response.results)
      setPagination({
        currentPage: response.current_page,
        totalPages: response.total_pages,
        totalCount: response.count,
        pageSize: response.page_size,
      })
    } catch (error) {
      console.error("Failed to fetch assignments:", error)
      toast.error("Failed to load assignments")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAssignments(1, search, statusFilter)
  }, [refreshTrigger])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    fetchAssignments(1, search, statusFilter)
  }

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status)
    fetchAssignments(1, search, status)
  }

  const handleRevoke = async (assignment: TurnstileAssignment) => {
    const result = await Swal.fire({
      title: "Revoke Assignment?",
      text: `Revoke turnstile ${assignment.turnstile_number} from ${assignment.user_name}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, revoke it!",
    })

    if (result.isConfirmed) {
      try {
        await TurnstileAssignmentService.revoke(assignment.id)
        await Swal.fire("Revoked!", "Assignment has been revoked.", "success")
        onRefresh()
      } catch (error) {
        console.error("Failed to revoke assignment:", error)
        await Swal.fire("Error", "Failed to revoke assignment.", "error")
      }
    }
  }

  const handleDelete = async (assignment: TurnstileAssignment) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Delete assignment for ${assignment.user_name}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    })

    if (result.isConfirmed) {
      try {
        await TurnstileAssignmentService.delete(assignment.id)
        await Swal.fire("Deleted!", "Assignment has been deleted.", "success")
        onRefresh()
      } catch (error) {
        console.error("Failed to delete assignment:", error)
        await Swal.fire("Error", "Failed to delete assignment.", "error")
      }
    }
  }

  const getStatusBadge = (status: string) => {
    const statusColors = {
      active: "success",
      revoked: "danger",
      pending: "warning",
    }
    return (
      <Badge color={statusColors[status as keyof typeof statusColors] || "secondary"} pill>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    )
  }

  const handlePageChange = (page: number) => {
    fetchAssignments(page, search, statusFilter)
  }

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPagination((prev) => ({ ...prev, pageSize: newPerPage }))
    fetchAssignments(page, search, statusFilter)
  }

  const columns: TableColumn<TurnstileAssignment>[] = [
    
    {
      name: "User",
      selector: (row) => row.user_name || "Unassigned",
      sortable: true,
      cell: (row) => <strong>{row.user_name || "Unassigned"}</strong>,
    },
    {
      name: "Turnstile",
      selector: (row) => row.turnstile_number || "",
      sortable: true,
      cell: (row) => <strong>{row.turnstile_number}</strong>,
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => getStatusBadge(row.status),
    },
    {
      name: "Created At",
      selector: (row) => row.created_at,
      sortable: true,
      cell: (row) => new Date(row.created_at).toLocaleDateString(),
    },
    {
      name: "Created By",
      selector: (row) => row.created_by_name || "System",
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex gap-1">
          <Btn color="info" size="xs" onClick={() => onEdit(row)} title="Edit">
            <FaEdit />
          </Btn>
          {row.status === "active" && (
            <Btn color="warning" size="xs" onClick={() => handleRevoke(row)} title="Revoke">
              <FaBan />
            </Btn>
          )}
          <Btn color="danger" size="xs" onClick={() => handleDelete(row)} title="Delete">
            <FaTrash />
          </Btn>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "150px",
    },
  ]

  return (
    <Card>
      <CardHeader>
        <Row className="align-items-center">
          <Col md="6">
            <h5 className="mb-0">Turnstile Assignments ({pagination.totalCount})</h5>
          </Col>
          <Col md="6" className="text-end">
            <Btn color="primary" size="sm" onClick={onAdd}>
              <FaPlus className="me-2" />
              New Assignment
            </Btn>
          </Col>
        </Row>
      </CardHeader>
      <CardBody>
        {/* Search and Filters */}
        <form onSubmit={handleSearch} className="mb-3">
          <Row>
            <Col md="4">
              <div className="input-group">
                <Input
                  type="text"
                  placeholder="Search assignments..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <button className="btn btn-outline-secondary btn-sm" type="submit">
                  <FaSearch />
                </button>
              </div>
            </Col>
            <Col md="3">
              <Input type="select" value={statusFilter} onChange={(e) => handleStatusFilter(e.target.value)}>
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="revoked">Revoked</option>
              </Input>
            </Col>
          </Row>
        </form>

        {/* DataTable */}
        <DataTable
          columns={columns}
          data={assignments}
          progressPending={loading}
          pagination
          paginationServer
          paginationTotalRows={pagination.totalCount}
          paginationDefaultPage={pagination.currentPage}
          paginationPerPage={pagination.pageSize}
          paginationRowsPerPageOptions={[10, 20, 30, 50]}
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          noDataComponent="No assignments found"
          striped
          highlightOnHover
          responsive
        />
      </CardBody>
    </Card>
  )
}

export default TurnstileAssignmentList
